import type {
  DisciplineStageComment,
  DisciplineStageCommentBroadcast,
  DisciplineStageCommentForm,
  DisciplineStageCommentPayload,
} from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { compareArrays } from '@shared/lib/collections'
import { useDayjs } from '@shared/lib/format'

const STAGE_COMMENT_SYSTEM_AGENT = '(System)'

const REACHED = 'REACHED'

interface DisciplineStageCommentLists {
  channels: Record<string, string>
  reach: Record<string, string>
}

export interface DisciplineStageCommentView {
  agentName: string
  isContacted: boolean
  channels: string[]
  reach: string | null
  isReached: boolean
}

export const buildStageCommentView = (
  comment: DisciplineStageComment,
  lists: DisciplineStageCommentLists,
): DisciplineStageCommentView => ({
  agentName: comment.agent?.name || STAGE_COMMENT_SYSTEM_AGENT,
  isContacted: comment.channel.length > 0 && !!comment.reach,
  channels: comment.channel.map(channel => lists.channels[channel] ?? channel),
  reach: comment.reach ? lists.reach[comment.reach] ?? comment.reach : null,
  isReached: comment.reach === REACHED,
})

export const createStageCommentForm = (comment?: DisciplineStageComment | null): DisciplineStageCommentForm => ({
  comment: comment?.comment ?? '',
  channel: [...(comment?.channel ?? [])],
  reach: comment?.reach ?? null,
})

/** A reach without a channel is never sent, so it does not count as a value either. */
const getSentReach = (form: DisciplineStageCommentForm): string | null =>
  form.channel.length ? form.reach : null

/** A comment needs text; an edit also needs a change to the text, the channels or the reach. */
export const isStageCommentSubmittable = (
  form: DisciplineStageCommentForm,
  initial: DisciplineStageCommentForm | null,
): boolean => {
  if (!form.comment.trim()) return false
  if (!initial) return true

  return form.comment !== initial.comment
    || !compareArrays(form.channel, initial.channel)
    || getSentReach(form) !== getSentReach(initial)
}

export const isStageCommentReachMissing = (form: DisciplineStageCommentForm): boolean =>
  form.channel.length > 0 && !form.reach

export const buildStageCommentPayload = (form: DisciplineStageCommentForm): DisciplineStageCommentPayload => {
  const hasChannels = form.channel.length > 0

  return {
    comment: form.comment,
    channel: hasChannels ? [...form.channel] : null,
    reach: hasChannels ? form.reach : null,
  }
}

const shiftTime = (value: string, fromTimezone: string, toTimezone: string): string => {
  if (!fromTimezone || !toTimezone || fromTimezone === toTimezone) return value

  return useDayjs().tz(value, fromTimezone).tz(toTimezone).format(DateFormat.Default)
}

/** The pushed comment in the shape and the admin view timezone of the list it joins. */
export const fromStageCommentBroadcast = (
  comment: DisciplineStageCommentBroadcast,
  customerTimezone: string,
  adminTimezone: string,
): DisciplineStageComment => ({
  id: comment.id,
  discipline_id: comment.discipline_id,
  comment: comment.comment,
  channel: comment.channel ?? [],
  reach: comment.reach,
  created_at: shiftTime(comment.created_at, customerTimezone, adminTimezone),
  updated_at: shiftTime(comment.updated_at, customerTimezone, adminTimezone),
  agent: comment.agent ? { id: comment.agent.id, name: comment.agent.name } : null,
})
